import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, MapPin } from 'lucide-react';
import LocationPicker from './LocationPicker';

export default function CreateRideModal({ onClose, onCreated, clubId = null }) {
  const [loading, setLoading] = useState(false);
  const [rideType, setRideType] = useState('individual');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [routeOptions, setRouteOptions] = useState([{ name: '', link: '' }]);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // Controlled form state to preserve values when location picker opens/closes
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    distance: '',
    max_riders: '',
    category: '',
    meeting_point: '',
    route_description: '',
    route_link: ''
  });

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addRouteOption = () => {
    if (routeOptions.length < 3) {
      setRouteOptions([...routeOptions, { name: '', link: '' }]);
    }
  };

  const removeRouteOption = (index) => {
    if (routeOptions.length > 1) {
      setRouteOptions(routeOptions.filter((_, i) => i !== index));
    }
  };

  const updateRouteOption = (index, field, value) => {
    const updated = [...routeOptions];
    updated[index][field] = value;
    setRouteOptions(updated);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      toast.error('Please select a start location');
      return;
    }
    
    setLoading(true);

    const data = {
      date: formData.date,
      time: formData.time,
      distance: parseInt(formData.distance),
      category: formData.category,
      // Location data
      latitude: selectedLocation.lat,
      longitude: selectedLocation.lng,
      formatted_address: selectedLocation.address,
      location: formData.meeting_point || '',
      postcode: '', // Keep for backward compatibility
      route_description: formData.route_description,
      max_riders: formData.max_riders ? parseInt(formData.max_riders) : null,
      ride_type: rideType,
      requires_approval: requiresApproval,
      // Club ride support
      club_id: clubId || null,
      is_club_only: !!clubId,
    };

    // For individual rides, use single route link
    if (rideType === 'individual') {
      data.route_link = formData.route_link || '';
    } else {
      // For group rides, use route options for voting
      const validRoutes = routeOptions.filter(r => r.name && r.link);
      if (validRoutes.length === 0) {
        toast.error('Please add at least one route option');
        setLoading(false);
        return;
      }
      data.route_options = validRoutes;
      data.route_link = validRoutes[0].link;
    }

    try {
      await axios.post('/rides', data);
      toast.success(`${clubId ? 'Club ride' : rideType === 'group' ? 'Group ride' : 'Ride'} created successfully!`);
      onCreated();
    } catch (error) {
      toast.error('Failed to create ride');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!showLocationPicker && (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
          <DialogContent 
            className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
            data-testid="create-ride-modal"
          >
          <DialogHeader>
            <DialogTitle>Create a New Ride</DialogTitle>
          </DialogHeader>
          
          <Tabs value={rideType} onValueChange={setRideType} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="individual">🚴 Individual</TabsTrigger>
              <TabsTrigger value="group">👥 Group Ride</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  value={formData.date}
                  onChange={(e) => updateFormField('date', e.target.value)}
                  data-testid="ride-date"
                />
              </div>
              <div>
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  name="time"
                  type="time"
                  required
                  value={formData.time}
                  onChange={(e) => updateFormField('time', e.target.value)}
                  data-testid="ride-time"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="distance">Distance (km)</Label>
                <Input
                  id="distance"
                  name="distance"
                  type="number"
                  required
                  min="5"
                  max="500"
                  placeholder="30"
                  value={formData.distance}
                  onChange={(e) => updateFormField('distance', e.target.value)}
                  data-testid="ride-distance-input"
                />
              </div>
              <div>
                <Label htmlFor="max_riders">Max Riders</Label>
                <Input
                  id="max_riders"
                  name="max_riders"
                  type="number"
                  min="1"
                  max="50"
                  placeholder={rideType === 'group' ? '10' : 'No limit'}
                  value={formData.max_riders}
                  onChange={(e) => updateFormField('max_riders', e.target.value)}
                  data-testid="ride-max-riders"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="category">Ride Type</Label>
              <select
                id="category"
                name="category"
                required
                value={formData.category}
                onChange={(e) => updateFormField('category', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white focus:border-purple-500 focus:ring-purple-500"
                data-testid="ride-category"
              >
                <option value="">Select ride type...</option>
                <option value="road">🛣️ Road</option>
                <option value="mountain">⛰️ Mountain</option>
                <option value="gravel">🏞️ Gravel</option>
                <option value="commute">🚴 Commute</option>
              </select>
            </div>

            {/* Start Location - Map picker */}
            <div>
              <Label>Start Location</Label>
              <Button
                type="button"
                variant="outline"
                className="w-full mt-1 justify-start text-left h-auto py-3"
                onClick={() => setShowLocationPicker(true)}
                data-testid="location-picker-button"
              >
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" />
                {selectedLocation ? (
                  <span className="truncate text-foreground">{selectedLocation.address}</span>
                ) : (
                  <span className="text-muted-foreground">Tap to select start location on map</span>
                )}
              </Button>
            </div>
            
            <div>
              <Label htmlFor="meeting_point">Meeting Point Details (Optional)</Label>
              <Input
                id="meeting_point"
                name="meeting_point"
                placeholder="e.g., Car park entrance, near the cafe"
                value={formData.meeting_point}
                onChange={(e) => updateFormField('meeting_point', e.target.value)}
                data-testid="meeting-point"
              />
            </div>

            <div>
              <Label htmlFor="route_description">Route Description</Label>
              <Textarea
                id="route_description"
                name="route_description"
                required
                placeholder="Describe the route, terrain, stops..."
                rows={2}
                value={formData.route_description}
                onChange={(e) => updateFormField('route_description', e.target.value)}
                data-testid="ride-route-input"
              />
            </div>

            {rideType === 'individual' ? (
              <div>
                <Label htmlFor="route_link">Route Link (Optional)</Label>
                <Input
                  id="route_link"
                  name="route_link"
                  type="url"
                  placeholder="https://connect.garmin.com/... or Strava link"
                  value={formData.route_link}
                  onChange={(e) => updateFormField('route_link', e.target.value)}
                  data-testid="ride-route-link"
                />
                <p className="text-xs text-muted-foreground mt-1">Garmin, Strava, or GPS link</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Route Options (for voting)</Label>
                  {routeOptions.length < 3 && (
                    <Button type="button" variant="outline" size="sm" onClick={addRouteOption}>
                      <Plus className="w-4 h-4 mr-1" /> Add Route
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Add up to 3 routes for riders to vote on before the ride
                </p>
                {routeOptions.map((route, index) => (
                  <div key={index} className="p-3 bg-gray-800/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-400">Route {index + 1}</span>
                      {routeOptions.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeRouteOption(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Route name (e.g., Scenic Hills)"
                      value={route.name}
                      onChange={(e) => updateRouteOption(index, 'name', e.target.value)}
                      required={rideType === 'group'}
                    />
                    <Input
                      placeholder="https://connect.garmin.com/... or Strava link"
                      value={route.link}
                      onChange={(e) => updateRouteOption(index, 'link', e.target.value)}
                      type="url"
                      required={rideType === 'group'}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
              <div>
                <Label htmlFor="requires_approval" className="text-sm font-medium">
                  Require Approval
                </Label>
                <p className="text-xs text-muted-foreground">
                  Review riders before they join
                </p>
              </div>
              <Switch
                id="requires_approval"
                checked={requiresApproval}
                onCheckedChange={setRequiresApproval}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={loading || !selectedLocation}>
                {loading ? 'Creating...' : `Create ${rideType === 'group' ? 'Group ' : ''}Ride`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Full-screen Location Picker */}
      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={selectedLocation}
      />
    </>
  );
}
